import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { X, Loader2, Check, Layers, RotateCcw } from "lucide-react";

/**
 * DV360 metric-access modal. Drives:
 *   - super_admin granting DV360 metric keys to an agency       (scope="agency")
 *   - admin / super_admin granting DV360 metric keys to an advertiser (scope="advertiser")
 *
 * Endpoints:
 *   GET  /dv360/allowed-metrics?agency_id={id}                  → {catalog, allowed_metrics, ...}
 *   PUT  /dv360/allowed-metrics/{id}
 *   GET  /dv360/advertiser-allowed-metrics/{id}                 → {catalog, available_metrics, allowed_metrics, ...}
 *   PUT  /dv360/advertiser-allowed-metrics/{id}
 */
export default function DV360MetricsModal({ scope, id, name, onClose }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [catalog, setCatalog] = useState([]);
    const [available, setAvailable] = useState([]);
    const [allowed, setAllowed] = useState([]);
    const [usesDefaults, setUsesDefaults] = useState(true);

    const getEndpoint = scope === "agency"
        ? `/dv360/allowed-metrics?agency_id=${id}`
        : `/dv360/advertiser-allowed-metrics/${id}`;
    const putEndpoint = scope === "agency"
        ? `/dv360/allowed-metrics/${id}`
        : `/dv360/advertiser-allowed-metrics/${id}`;
    const headerLabel = scope === "agency" ? "Agency · DV360 metrics access" : "Advertiser · DV360 metrics access";
    const subtitle = scope === "agency"
        ? "Pick which DV360 detailed-report metrics this agency may see. Advertisers under this agency cannot be granted more than what you allow here."
        : "Pick which DV360 detailed-report metrics this advertiser may see. Metrics the agency hasn't been granted are shown disabled.";

    useEffect(() => {
        let cancelled = false;
        async function load() {
            setLoading(true);
            try {
                const { data } = await api.get(getEndpoint);
                if (cancelled) return;
                setCatalog(data?.catalog || []);
                setAvailable(scope === "agency" ? (data?.catalog || []).map(c => c.key) : (data?.available_metrics || []));
                setAllowed(data?.allowed_metrics || []);
                setUsesDefaults(!!data?.uses_defaults);
            } catch (e) {
                toast.error(e.response?.data?.detail || "Failed to load metrics");
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        load();
        return () => { cancelled = true; };
    }, [getEndpoint, scope]);

    const toggle = (key) => {
        if (!available.includes(key)) return;
        setAllowed((p) => p.includes(key) ? p.filter((k) => k !== key) : [...p, key]);
    };
    const selectAll = () => setAllowed([...available]);
    const clearAll = () => setAllowed([]);
    const resetDefaults = () => setAllowed([...available]);

    const save = async () => {
        setSaving(true);
        try {
            await api.put(putEndpoint, { allowed_metrics: allowed });
            toast.success(`DV360 metrics updated for ${name}`);
            onClose?.(true);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/50" data-testid="dv360-metric-modal">
            <div className="bg-white border border-zinc-200 rounded-lg shadow-xl w-full max-w-xl mx-4 max-h-[85vh] flex flex-col">
                {/* Header */}
                <div className="flex items-start justify-between p-5 border-b border-zinc-200">
                    <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-md wl-accent-bg flex items-center justify-center text-white">
                            <Layers className="h-5 w-5" strokeWidth={2.5} />
                        </div>
                        <div>
                            <div className="overline">{headerLabel}</div>
                            <h2 className="font-display text-lg text-zinc-900 truncate max-w-md" data-testid="dv360-metric-title">{name}</h2>
                            <p className="text-xs text-zinc-500 mt-1 max-w-md">{subtitle}</p>
                        </div>
                    </div>
                    <button onClick={() => onClose?.(false)} className="p-1 rounded hover:bg-zinc-100 text-zinc-500" data-testid="dv360-metric-close" aria-label="Close">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-5">
                    {loading ? (
                        <div className="flex items-center justify-center gap-2 text-zinc-500 py-12">
                            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center justify-between mb-3">
                                <div className="text-xs text-zinc-500">
                                    <span className="tabular-nums">{allowed.length}</span> / <span className="tabular-nums">{available.length}</span> selected
                                    {usesDefaults && <span className="ml-2 text-zinc-400">· using defaults</span>}
                                </div>
                                <div className="flex items-center gap-2 text-[11px]">
                                    <button onClick={selectAll} className="wl-accent-text hover:underline" data-testid="dv360-metric-all">Select all</button>
                                    <span className="text-zinc-300">·</span>
                                    <button onClick={clearAll} className="text-zinc-500 hover:text-zinc-900" data-testid="dv360-metric-clear">Clear</button>
                                    <span className="text-zinc-300">·</span>
                                    <button onClick={resetDefaults} className="inline-flex items-center gap-1 text-zinc-500 hover:text-zinc-900" data-testid="dv360-metric-defaults"><RotateCcw className="h-3 w-3" />Defaults</button>
                                </div>
                            </div>
                            <div className="border border-zinc-200 rounded divide-y divide-zinc-100">
                                {catalog.map((c) => {
                                    const isAvailable = available.includes(c.key);
                                    const isAllowed = allowed.includes(c.key);
                                    return (
                                        <label
                                            key={c.key}
                                            className={`flex items-center gap-3 px-3 py-2.5 text-sm ${!isAvailable ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:bg-zinc-50"}`}
                                            data-testid={`dv360-metric-row-${c.key}`}
                                        >
                                            <span className={`h-4 w-4 rounded border flex items-center justify-center ${isAllowed ? "wl-accent-bg wl-accent-border" : "border-zinc-300 bg-white"}`}>
                                                {isAllowed && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                                            </span>
                                            <input
                                                type="checkbox"
                                                className="sr-only"
                                                checked={isAllowed}
                                                onChange={() => toggle(c.key)}
                                                disabled={!isAvailable}
                                            />
                                            <span className="text-zinc-900 flex-1">{c.label}</span>
                                            {!isAvailable && (
                                                <span className="text-[10px] text-zinc-400 italic">not granted to agency</span>
                                            )}
                                        </label>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 p-4 border-t border-zinc-200 bg-zinc-50/50">
                    <button onClick={() => onClose?.(false)} className="px-3 py-2 rounded text-sm font-medium border border-zinc-300 hover:bg-zinc-100 text-zinc-700" data-testid="dv360-metric-cancel">Cancel</button>
                    <button onClick={save} disabled={loading || saving} className="inline-flex items-center gap-1.5 px-4 py-2 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-sm" style={{ background: "var(--wl-primary-hex)", color: "#fff" }} data-testid="dv360-metric-save">
                        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                        {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}
